/**
 * Merge AI `parseShipment` payload into a loaded quote (empty-only: never overwrite non-empty fields).
 */

function isValueMissing(value) {
  const display =
    typeof value === "string" ? value : value?.toString?.() ?? value;
  return (
    display === null ||
    display === undefined ||
    (typeof display === "string" && display.trim() === "")
  );
}

// Numeric package fields (weight/length/width/height/quantity) come from the
// backend's minimal-quote placeholder as literal `0`, which is not "missing"
// to isValueMissing — but for AI-parse merge purposes a zero is meaningless
// and must be overwriteable. Anything > 0 is a real user-supplied value.
function isPackageNumberMissing(value) {
  if (isValueMissing(value)) return true;
  const n = Number(value);
  return !Number.isFinite(n) || n <= 0;
}

// Descriptions the placeholder package row uses before AI runs. Treat any of
// these as "no description" so AI-extracted text replaces them.
const PLACEHOLDER_DESCRIPTIONS = new Set([
  "",
  " ",
  "Package",
  "Air freight package",
]);
function isPackageDescriptionMissing(value) {
  if (isValueMissing(value)) return true;
  return PLACEHOLDER_DESCRIPTIONS.has(String(value).trim());
}

function mergeScalar(prev, next) {
  if (!isValueMissing(prev)) return prev;
  if (next == null || next === "") return prev;
  return typeof next === "string" ? next : next;
}

function mapParsedPkg(p) {
  if (!p || typeof p !== "object") return null;
  return {
    description: p.description || "Package",
    quantity: Math.max(1, Number(p.quantity) || 1),
    weight: p.weight === "" || p.weight == null ? "" : Number(p.weight) || "",
    length: p.length === "" || p.length == null ? "" : Number(p.length) || "",
    width: p.width === "" || p.width == null ? "" : Number(p.width) || "",
    height: p.height === "" || p.height == null ? "" : Number(p.height) || "",
    stackable: Boolean(p.stackable),
    hazardous: Boolean(p.hazardous),
  };
}

function packageRowEmpty(pkg) {
  if (!pkg) return true;
  return (
    isPackageNumberMissing(pkg.weight) &&
    isPackageNumberMissing(pkg.length) &&
    isPackageNumberMissing(pkg.width) &&
    isPackageNumberMissing(pkg.height) &&
    isPackageDescriptionMissing(pkg.description)
  );
}

/**
 * @returns {{ nextQuote: object, changedFields: Array<{ id: string, label: string, oldValue: string, newValue: string }>, confidence: Record<string, number> }}
 */
export function applyAiParsedToQuote(quote, parsed) {
  const next = JSON.parse(JSON.stringify(quote));
  const cd = { ...(next.containerDetails || {}) };
  const rc = { ...(cd.receiverContactDetails || {}) };
  const cust = { ...(next.customerDetails || {}) };
  const changed = [];
  const confFlat = { ...(parsed?._confidence && typeof parsed._confidence === "object" ? parsed._confidence : {}) };

  const pushChange = (id, label, oldVal, newVal) => {
    const o = oldVal == null ? "" : String(oldVal);
    const n = newVal == null ? "" : String(newVal);
    if (o !== n) changed.push({ id, label, oldValue: o, newValue: n });
  };

  const o0 = cd.origin;
  cd.origin = mergeScalar(cd.origin, parsed.origin);
  if (cd.origin !== o0) pushChange("origin", "Origin", o0, cd.origin);

  const d0 = cd.destination;
  cd.destination = mergeScalar(cd.destination, parsed.destination);
  if (cd.destination !== d0) pushChange("destination", "Destination", d0, cd.destination);

  const r0 = cd.requirements;
  cd.requirements = mergeScalar(cd.requirements, parsed.requirements);
  if (cd.requirements !== r0) pushChange("requirements", "Requirements", r0, cd.requirements);

  const n0 = cust.name;
  cust.name = mergeScalar(cust.name, parsed.customerName);
  if (cust.name !== n0) pushChange("customerName", "Customer name", n0, cust.name);

  const e0 = cust.email;
  cust.email = mergeScalar(cust.email, parsed.customerEmail);
  if (cust.email !== e0) pushChange("customerEmail", "Customer email", e0, cust.email);

  const p0 = cust.phone;
  cust.phone = mergeScalar(cust.phone, parsed.customerPhone);
  if (cust.phone !== p0) pushChange("customerPhone", "Customer phone", p0, cust.phone);

  const rn0 = rc.name;
  rc.name = mergeScalar(rc.name, parsed.customerName);
  if (rc.name !== rn0) pushChange("receiverContactDetails.name", "Receiver name", rn0, rc.name);

  const re0 = rc.email;
  rc.email = mergeScalar(rc.email, parsed.customerEmail);
  if (rc.email !== re0) pushChange("receiverContactDetails.email", "Receiver email", re0, rc.email);

  const rp0 = rc.phone;
  rc.phone = mergeScalar(rc.phone, parsed.customerPhone);
  if (rc.phone !== rp0) pushChange("receiverContactDetails.phone", "Receiver phone", rp0, rc.phone);

  cd.receiverContactDetails = rc;
  next.customerDetails = cust;

  const prevPkgs = Array.isArray(cd.packages) ? cd.packages : [];
  const parsedPkgs = Array.isArray(parsed.packages) ? parsed.packages.map(mapParsedPkg).filter(Boolean) : [];
  const allPrevEmpty = prevPkgs.length === 0 || prevPkgs.every((pkgRow) => packageRowEmpty(pkgRow));

  if (parsedPkgs.length > 0) {
    if (allPrevEmpty) {
      if (JSON.stringify(prevPkgs) !== JSON.stringify(parsedPkgs)) {
        pushChange("packages", "Packages", "(empty)", `${parsedPkgs.length} row(s)`);
      }
      cd.packages = parsedPkgs;
    } else {
      const NUMERIC_KEYS = new Set(["quantity", "weight", "length", "width", "height"]);
      const merged = prevPkgs.map((row, i) => {
        const src = parsedPkgs[i];
        if (!src) return row;
        const out = { ...row };
        ["description", "quantity", "weight", "length", "width", "height", "stackable", "hazardous"].forEach(
          (k) => {
            if (k === "stackable" || k === "hazardous") {
              // Booleans: backend default is `stackable: true, hazardous: false`,
              // which read as real values to isValueMissing. Don't overwrite
              // these — operator can flip manually.
              return;
            }
            if (k === "description") {
              if (!isPackageDescriptionMissing(row?.[k])) return;
              if (src[k] != null && String(src[k]).trim()) out[k] = src[k];
              return;
            }
            if (NUMERIC_KEYS.has(k)) {
              // Treat 0 / empty / non-finite as missing so AI values overwrite
              // the placeholder zeros from buildMinimalQuotePayload.
              if (!isPackageNumberMissing(row?.[k])) return;
              if (src[k] !== "" && src[k] != null) out[k] = src[k];
              return;
            }
            if (!isValueMissing(row?.[k])) return;
            out[k] = src[k];
          }
        );
        return out;
      });
      if (parsedPkgs.length > prevPkgs.length) {
        for (let i = prevPkgs.length; i < parsedPkgs.length; i += 1) {
          merged.push(parsedPkgs[i]);
        }
        pushChange("packages", "Packages", String(prevPkgs.length), String(merged.length));
      }
      cd.packages = merged;
    }
  }

  next.containerDetails = cd;

  const confidence = {};
  ["origin", "destination", "requirements", "customerName", "customerEmail", "customerPhone"].forEach((k) => {
    if (confFlat[k] != null && Number.isFinite(Number(confFlat[k]))) confidence[k] = Number(confFlat[k]);
  });

  return { nextQuote: next, changedFields: changed, confidence };
}

export function restoreFieldFromSnapshot(quote, snapshot, fieldId) {
  const next = JSON.parse(JSON.stringify(quote));
  if (!snapshot) return next;
  const snapCd = snapshot.containerDetails || {};
  const snapCust = snapshot.customerDetails || {};
  const snapRc = snapCd.receiverContactDetails || {};

  if (fieldId === "origin") next.containerDetails = { ...next.containerDetails, origin: snapCd.origin };
  else if (fieldId === "destination")
    next.containerDetails = { ...next.containerDetails, destination: snapCd.destination };
  else if (fieldId === "requirements")
    next.containerDetails = { ...next.containerDetails, requirements: snapCd.requirements };
  else if (fieldId === "customerName")
    next.customerDetails = { ...next.customerDetails, name: snapCust.name };
  else if (fieldId === "customerEmail")
    next.customerDetails = { ...next.customerDetails, email: snapCust.email };
  else if (fieldId === "customerPhone")
    next.customerDetails = { ...next.customerDetails, phone: snapCust.phone };
  else if (fieldId === "receiverContactDetails.name") {
    next.containerDetails = {
      ...next.containerDetails,
      receiverContactDetails: {
        ...(next.containerDetails?.receiverContactDetails || {}),
        name: snapRc.name,
      },
    };
  } else if (fieldId === "receiverContactDetails.email") {
    next.containerDetails = {
      ...next.containerDetails,
      receiverContactDetails: {
        ...(next.containerDetails?.receiverContactDetails || {}),
        email: snapRc.email,
      },
    };
  } else if (fieldId === "receiverContactDetails.phone") {
    next.containerDetails = {
      ...next.containerDetails,
      receiverContactDetails: {
        ...(next.containerDetails?.receiverContactDetails || {}),
        phone: snapRc.phone,
      },
    };
  } else if (fieldId === "packages") {
    next.containerDetails = {
      ...next.containerDetails,
      packages: Array.isArray(snapCd.packages) ? JSON.parse(JSON.stringify(snapCd.packages)) : [],
    };
  }
  return next;
}
